import { zhCN } from 'date-fns/locale'
import { Calculator, Calendar, X } from 'lucide-react'
import React, { useEffect, useState } from 'react'
import ReactDatePicker, { registerLocale } from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import "../../assets/styles/datepicker.css"

// 注册中文语言包
registerLocale('zh-CN', zhCN)

interface FreetimePickerProps {
  label: string
  date: string
  freetimeDate?: string // Freetime日期 (YYYY-MM-DD format)
  onDateChange?: (date: string) => void
  disabled?: boolean
  required?: boolean
  className?: string
}

const FreetimePicker: React.FC<FreetimePickerProps> = ({
  label,
  date,
  freetimeDate,
  onDateChange,
  disabled = false,
  required = false,
  className = ''
}) => {
  const [inputMode, setInputMode] = useState<'date' | 'days'>('date') // 输入模式：日期或天数
  const [localDays, setLocalDays] = useState<number>(7) // 本地天数状态
  const [localDate, setLocalDate] = useState<string>(freetimeDate || '') // 本地日期

  // 计算两个日期之间的天数差
  const calculateDaysDifference = (
    startDate: string,
    endDate: string
  ): number => {
    const start = new Date(startDate)
    const end = new Date(endDate)
    const diffTime = end.getTime() - start.getTime()
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24))
  }

  // 根据基准日期和天数计算目标日期
  const calculateTargetDate = (startDate: string, days: number): string => {
    const start = new Date(startDate)
    start.setDate(start.getDate() + days)
    return start.toISOString().split('T')[0]
  }

  // 当 Freetime 日期改变时，计算天数
  const handleDateChange = (selectDate: Date | null) => {
    let newDate = ''
    if (selectDate) {
      const year = selectDate.getFullYear()
      const month = String(selectDate.getMonth() + 1).padStart(2, '0')
      const day = String(selectDate.getDate()).padStart(2, '0')
      newDate = `${year}-${month}-${day}`
    }

    // 更新本地日期状态
    setLocalDate(newDate)
    
    // 通知父组件日期变化
    onDateChange?.(newDate)

    // 同时调用天数回调以保持同步，但由 useDateHandlers 处理防重复
    if (newDate) {
      const days = calculateDaysDifference(date, newDate)
      setLocalDays(days)
    } else {
      // 清空日期时，重置天数为默认值
      setLocalDays(7)
    }
  }

  // 当天数改变时，只更新本地状态
  const handleDaysInputChange = (newDays: number) => {
    setLocalDays(newDays)
  }

  // 当外部传入的freetimeDate改变时，同步到本地状态
  useEffect(() => {
    setLocalDate(freetimeDate || '')
    // 如果有外部日期且有基准日期，计算天数
    if (freetimeDate && date) {
      const days = calculateDaysDifference(date, freetimeDate)
      setLocalDays(days > 0 ? days : 7)
    }
  }, [freetimeDate, date])

  // 当基准日期改变时，重新计算 Freetime 日期（仅当没有外部日期时）
  useEffect(() => {
    if (date && localDays > 0 && !freetimeDate) {
      const targetDate = calculateTargetDate(date, localDays)
      setLocalDate(targetDate)
      onDateChange?.(targetDate)
    }
  }, [date, localDays, freetimeDate])

  return (
    <div className={`space-y-2 ${className}`}>
      <label className="block text-sm font-medium text-gray-700">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>

      {/* 主要输入区域 - 与其他DatePicker高度一致 */}
      <div className="flex items-center space-x-2">
        <div className="relative flex-1">
          {/* 左侧图标 */}
          {inputMode === 'date' ? (
            <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none z-10" />
          ) : (
            <Calculator className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none z-10" />
          )}

          {/* 条件渲染：日期输入或天数输入 */}
          {inputMode === 'date' ? (
            <ReactDatePicker
              selected={localDate ? new Date(localDate) : null}
              onChange={handleDateChange}
              disabled={disabled || !localDate}
              placeholderText="请选择日期"
              dateFormat="yyyy年MM月dd日"
              className={`
                w-full pl-10 ${
                  localDate ? 'pr-8' : 'pr-3'
                } py-2 border border-gray-300 rounded-md
                focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500
                disabled:bg-gray-100 disabled:cursor-not-allowed
                ${!localDate ? 'text-gray-400' : 'text-gray-900'}
              `}
              wrapperClassName="w-full"
              popperClassName="z-50"
              showPopperArrow={false}
              fixedHeight
              locale="zh-CN"
              todayButton="今天"
              isClearable={false}
              showYearDropdown
              showMonthDropdown
              dropdownMode="select"
              calendarStartDay={1}
              minDate={date ? new Date(date) : undefined}
            />
          ) : (
            <div className="relative">
              <input
                type="number"
                min="0"
                max="365"
                value={localDays || ''}
                onChange={(e) =>
                  handleDaysInputChange(parseInt(e.target.value) || 0)
                }
                disabled={disabled}
                placeholder="默认7天"
                className={`
                  w-full pl-10 pr-12 py-2 border border-gray-300 rounded-md
                  focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500
                  disabled:bg-gray-100 disabled:cursor-not-allowed
                `}
              />
              <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-sm text-gray-500 pointer-events-none">
                天
              </span>
            </div>
          )}

          {/* 输入框内的清除按钮 - 只在日期模式下显示 */}
          {inputMode === 'date' && localDate && (
            <button
              type="button"
              onClick={() => handleDateChange(null)}
              className="absolute right-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 hover:text-gray-600 transition-colors z-10"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* 模式切换按钮 - 放在输入框外面 */}
        <button
          type="button"
          onClick={() => setInputMode(inputMode === 'date' ? 'days' : 'date')}
          className="flex-shrink-0 p-2 text-blue-500 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
          title={inputMode === 'date' ? '切换到天数输入' : '切换到日期输入'}
        >
          {inputMode === 'date' ? (
            <Calculator className="w-4 h-4" />
          ) : (
            <Calendar className="w-4 h-4" />
          )}
        </button>
      </div>

      {/* 状态提示 */}
      {/* <div className="space-y-1">
        {
          <p className="text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded">
            请先选择预计入库日期
          </p>
        }

        {freetimeDate && (
          <p className="text-xs text-gray-500">
            预计入库日期后 {localDays || 0} 天
          </p>
        )}

        {!freetimeDate && (
          <p className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded">
            系统将自动设置为7天后
          </p>
        )}
      </div> */}
    </div>
  )
}

export default FreetimePicker
